import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CommandCentreResponse } from "@/api/intelligence";
import { formatChartDate, formatInr } from "@/lib/intelligenceFormat";
import { KpiCard } from "./KpiCard";

type Props = {
  data: CommandCentreResponse;
};

const FINANCIAL_KEYS = ["gross_billing", "net_revenue", "collections", "outstanding"] as const;

export function RevenuePerformanceSection({ data }: Props) {
  const financialKpis = FINANCIAL_KEYS.map(
    (key) => data.kpis.find((k) => k.key === key)!
  ).filter(Boolean);

  const chartData = useMemo(() => {
    const labels = data.revenue.timeSeries.map((p) => formatChartDate(p.date));
    return {
      labels,
      datasets: [
        {
          label: "Gross Billing",
          data: data.revenue.timeSeries.map((p) => p.grossBilling),
          borderColor: "rgb(99, 102, 241)",
          backgroundColor: "rgba(99, 102, 241, 0.08)",
          tension: 0.25,
          fill: false,
        },
        {
          label: "Net Revenue",
          data: data.revenue.timeSeries.map((p) => p.netRevenue),
          borderColor: "rgb(16, 185, 129)",
          backgroundColor: "rgba(16, 185, 129, 0.08)",
          tension: 0.25,
          fill: false,
        },
        {
          label: "Collections",
          data: data.revenue.timeSeries.map((p) => p.collections),
          borderColor: "rgb(245, 158, 11)",
          backgroundColor: "rgba(245, 158, 11, 0.08)",
          tension: 0.25,
          fill: false,
        },
      ],
    };
  }, [data.revenue.timeSeries]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { position: "bottom" as const },
        tooltip: {
          callbacks: {
            label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) =>
              `${ctx.dataset.label}: ${formatInr(ctx.parsed.y ?? 0)}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (v: string | number) => formatInr(Number(v)),
          },
        },
      },
    }),
    []
  );

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Revenue Performance</h2>
        <p className="text-sm text-muted-foreground">
          {data.period.label}
          {data.dataQuality.hasPartialRollups && " · Some days computed live (rollup partial)"}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {financialKpis.map((kpi) => (
          <KpiCard key={kpi.key} kpi={kpi} comparisonLabel={data.comparisonPeriod?.label} />
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Daily trend</CardTitle>
        </CardHeader>
        <CardContent>
          {data.revenue.timeSeries.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No revenue data for this period</p>
          ) : (
            <div className="h-[280px]">
              <Line data={chartData} options={chartOptions} />
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
